import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Stethoscope, Users, CreditCard, KeyRound, Lock, BadgeIndianRupee } from 'lucide-react';
import useAxiosAuth from '../hooks/useAxiosAuth';
import useTenantFeatures from '../hooks/useTenantFeatures';
import AdminPaymentsTab from '../components/adminTabs/AdminPaymentsTab';
import PlatformPaymentsTab from '../components/adminTabs/PlatformPaymentsTab';
import TenantSubscriptionTab from '../components/adminTabs/TenantSubscriptionTab';
import { toast } from 'react-toastify';
import {
  FEATURE_KEYS,
  LEAVE_FUTURE_LIMIT_OPTIONS,
  toggleFeatureStatus,
  setFeatureValue,
} from '../redux/featureSlice';

// Per-tab toggle config. The fekey is the source of truth — it matches the
// row in the backend Feature table and the gating call-sites in the Sidebar
// and appointment screens.
const TAB_CONFIG = {
  // Everything that shapes the ONE shared login/sign-up surface. Platform-wide
  // by nature (tenant is a dropdown on that page), so every item here is
  // superAdminOnly — a tenant admin sees the "platform-wide settings" empty
  // state instead.
  login: {
    icon: KeyRound,
    toggles: [
      // Doctor registration is no longer a platform toggle: each tenant says
      // whether it accepts voluntary doctor sign-up (Super Admin → tenant
      // form → Login Policy; "Concept 2", 1 Oct 2026).
      {
        fekey: FEATURE_KEYS.authPatientRegistration,
        superAdminOnly: true,
        label: 'Patient Registration',
        description:
          "Show the public pointers to patient sign-up on the Mitra One Care patient portal (registration itself happens there; hospital desks create accounts for isolated tenants). When off, the patient card and the CTAs that lead to it are hidden across the home page and login screen.",
      },
    ],
    // The login-identifier policy is PER HOSPITAL now (email / mobile / both +
    // the two OTP sub-options) and super-admin-managed: it is set from
    // Tenants → open a tenant → Features → "Login & registration policy",
    // never from this page — the write path here refuses those keys.
  },
  doctor: {
    icon: Stethoscope,
    toggles: [
      {
        fekey: FEATURE_KEYS.doctorAllPatientMode,
        tenantOwned: true,
        label: 'All Patient Mode',
        description:
          "Show the “All Patients” option in the doctor's appointment modal so they can book for any patient, not just their own. Off, the server also keeps the hospital-wide patient list from doctors: each sees their own patients.",
      },
      // Patient Library removed (India-SaaS decisions log #18): the
      // tenant-scoped Patients page is the library.
      // Medicines is no longer a platform switch (owner, 1 Oct 2026): it is
      // always on, gated only by the tenant feature 'medicines'.
    ],
    // Multi-value settings rendered as a stepped slider below the toggles.
    sliders: [
      {
        fekey: FEATURE_KEYS.doctorLeaveFutureLimit,
        tenantOwned: true,
        label: 'Leave Apply Future Upto',
        description:
          'How far ahead doctors may apply for leave in the leave calendar. The leave date picker caps its selectable dates to this window. “No restriction” removes the cap.',
        options: LEAVE_FUTURE_LIMIT_OPTIONS,
        defaultValue: '12',
      },
    ],
  },
  // The patient platform switches (All Doctor Mode, Doctor Library, Family
  // Accounts) were retired on 1 Oct 2026 — family accounts are always on and
  // the other two were removed together with their UI.
};

const ToggleSwitch = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    disabled={disabled}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-offset-2 ${
      checked ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8]' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      aria-hidden="true"
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// Stepped progress-bar slider for multi-value settings. Renders a filled teal
// track up to the selected stop, clickable dots at every stop, a draggable knob,
// and an invisible range input on top for pointer-drag and keyboard control.
const SegmentedSlider = ({ options, value, onChange, disabled, testId }) => {
  const found = options.findIndex((o) => o.value === value);
  const idx = found === -1 ? 0 : found;
  const lastIdx = options.length - 1;
  const pct = lastIdx === 0 ? 0 : (idx / lastIdx) * 100;

  return (
    <div className="w-full select-none" data-testid={testId}>
      <div className="relative h-6">
        {/* base track */}
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200" />
        {/* filled progress */}
        <div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition-all duration-200"
          style={{ width: `${pct}%` }}
        />
        {/* stop dots */}
        {options.map((o, i) => {
          const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
          const reached = i <= idx;
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(o.value)}
              data-testid={`${testId}-stop-${o.value}`}
              aria-label={o.label}
              className={`absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white transition-colors ${
                reached ? 'border-[#4ABDBD]' : 'border-gray-300'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ left: `${left}%` }}
            />
          );
        })}
        {/* knob at the selected stop */}
        <div
          className="pointer-events-none absolute top-1/2 z-20 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md ring-2 ring-[#4ABDBD] transition-all duration-200"
          style={{ left: `${pct}%` }}
        />
        {/* invisible range for drag + keyboard */}
        <input
          type="range"
          min={0}
          max={lastIdx}
          step={1}
          value={idx}
          disabled={disabled}
          onChange={(e) => onChange(options[Number(e.target.value)].value)}
          data-testid={`${testId}-range`}
          className={`absolute inset-0 z-30 h-full w-full opacity-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        />
      </div>
      {/* stop labels */}
      <div className="relative mt-3 h-5">
        {options.map((o, i) => {
          const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
          const active = i === idx;
          const transform =
            i === 0 ? 'translateX(0)' : i === lastIdx ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <span
              key={o.value}
              className={`absolute top-0 whitespace-nowrap text-xs ${
                active ? 'font-semibold text-[#3AA8A8]' : 'text-gray-400'
              }`}
              style={{ left: `${left}%`, transform }}
            >
              {o.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// The hospital's own patient numbering (APO001 …): the tenant admin chooses a
// 2–5 letter prefix ONCE. Saving numbers every existing patient oldest-first
// and LOCKS the choice the moment the first patient holds a code — only
// platform support (Super Admin) can change it after that, and codes then
// keep their number. A custom card rather than a feature toggle: it writes a
// tenant column through its own endpoint (/tenants/my-patient-code), not a
// feature flag, so the flag plumbing above has nothing for it.
const PatientCodeCard = () => {
  const { t } = useTranslation();
  const axiosInstance = useAxiosAuth();
  const [info, setInfo] = useState({ loading: true, prefix: null, locked: false, sample: 'APO001' });
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axiosInstance.get('/tenants/my-patient-code');
      setInfo({ loading: false, prefix: data.prefix || null, locked: !!data.locked, sample: data.sample || 'APO001' });
      setDraft(data.prefix || '');
    } catch {
      // The card shows the empty form; a failed read must not block the page.
      setInfo((s) => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleaned = draft.trim().toUpperCase();
  const valid = /^[A-Z]{2,5}$/.test(cleaned);
  const unchanged = info.prefix && cleaned === info.prefix;

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/tenants/my-patient-code', { prefix: cleaned });
      toast.success(
        data.assigned
          ? t('settings.patientCode.savedAssigned', 'Prefix saved — {{count}} existing patients were numbered', { count: data.assigned })
          : t('settings.patientCode.saved', 'Prefix saved')
      );
      await load();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'PREFIX_TAKEN') {
        toast.error(t('settings.patientCode.taken', 'This prefix is already used by another hospital'));
      } else if (code === 'PREFIX_LOCKED') {
        toast.error(t('settings.patientCode.lockedToast', 'The prefix is locked — patients already carry it. Contact platform support to change it.'));
        await load();
      } else {
        toast.error(err?.response?.data?.message || t('settings.updateFailed', 'Failed to update setting'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 p-4 sm:p-5 mb-6" data-testid="settings-patient-code-card">
      <div className="min-w-0">
        <div className="font-semibold text-gray-800 text-sm sm:text-base" data-testid="settings-patient-code-label">
          {t('settings.patientCode.label', 'Patient ID prefix')}
        </div>
        <p className="text-xs sm:text-sm text-gray-500 mt-1" data-testid="settings-patient-code-description">
          {t('settings.patientCode.description',
            'Your hospital’s own patient numbering — 2–5 letters, e.g. APO gives APO001, APO002… Saving numbers all existing patients (oldest first) and new patients automatically. Once the first patient is numbered the prefix is locked; contact platform support to change it.')}
        </p>
      </div>
      {info.loading ? (
        <p className="text-xs text-gray-400 mt-4" data-testid="settings-patient-code-loading">
          {t('settings.patientCode.loading', 'Loading…')}
        </p>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={draft}
            maxLength={5}
            disabled={info.locked || saving}
            onChange={(e) => setDraft(e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase())}
            placeholder={t('settings.patientCode.placeholder', 'e.g. APO')}
            data-testid="settings-patient-code-input"
            className={`w-32 p-3 text-sm font-mono font-semibold tracking-widest rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent ${info.locked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
          />
          {info.locked ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500" data-testid="settings-patient-code-locked">
              <Lock className="w-3.5 h-3.5" />
              {t('settings.patientCode.locked', 'Locked — patients are numbered {{sample}}…', { sample: info.sample })}
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={save}
                disabled={!valid || unchanged || saving}
                data-testid="settings-patient-code-save"
                className={`px-5 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-300 ${(!valid || unchanged || saving)
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] shadow-md cursor-pointer'}`}
              >
                {saving ? t('settings.patientCode.saving', 'Saving…') : t('settings.patientCode.save', 'Save prefix')}
              </button>
              {valid && (
                <span className="text-xs text-gray-400" data-testid="settings-patient-code-preview">
                  {t('settings.patientCode.preview', 'Patients will be numbered {{sample}}…', { sample: `${cleaned}001` })}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const flags = useSelector((state) => state.feature?.flags || {});
  const featureLoading = useSelector((state) => state.feature?.loading);
  const toggling = useSelector((state) => state.feature?.toggling);
  const settingValue = useSelector((state) => state.feature?.settingValue);
  const { has: hasFeature, billing } = useTenantFeatures();

  // Super Admin lands on the Login tab (every control there is theirs); a
  // tenant admin starts on Doctor, since Login would only show the
  // "platform-wide settings" empty state — unless this tenant has no
  // medi_consult, in which case Doctor is hidden too and Payments is the
  // first tab with anything tenant-owned in it.
  const [activeTab, setActiveTab] = useState(() => {
    if (localStorage.getItem('isSuperAdmin') === 'true') return 'login';
    return hasFeature('appointments') ? 'doctor' : 'payments';
  });

  // Human label for a slider stop. Options with their own labelKey (e.g. the
  // login-identifier modes) resolve it directly; leave-future-limit stops keep
  // the count-based "3 months" … / "No restriction" phrasing.
  const sliderOptionLabel = (option) => {
    if (option.labelKey) return t(option.labelKey, option.labelFallback || option.value);
    return option.months === null
      ? t('settings.leaveFutureLimit.noRestriction', 'No restriction')
      : t('settings.leaveFutureLimit.months', '{{count}} months', { count: option.months });
  };

  const handleToggle = async (fekey) => {
    const flag = flags[fekey];
    if (!flag?.featureId) {
      toast.warn(
        t(
          'settings.flagNotProvisioned',
          'This setting is not yet provisioned in the database. Re-run the seed (npm run db:seed) to create it.'
        )
      );
      return;
    }
    try {
      await dispatch(toggleFeatureStatus({ featureId: flag.featureId, fekey })).unwrap();
      toast.success(t('settings.updated', 'Setting updated'));
    } catch (err) {
      toast.error(err?.error || t('settings.updateFailed', 'Failed to update setting'));
    }
  };

  const handleSliderChange = async (fekey, value) => {
    const flag = flags[fekey];
    if (!flag?.featureId) {
      toast.warn(
        t(
          'settings.flagNotProvisioned',
          'This setting is not yet provisioned in the database. Re-run the seed (npm run db:seed) to create it.'
        )
      );
      return;
    }
    if (flag.value === value) return; // already at this stop — no-op
    try {
      await dispatch(setFeatureValue({ featureId: flag.featureId, fekey, value })).unwrap();
      toast.success(t('settings.updated', 'Setting updated'));
    } catch (err) {
      toast.error(err?.error || t('settings.updateFailed', 'Failed to update setting'));
    }
  };

  // 'payments' is a custom tab (rendered by AdminPaymentsTab), so it has no
  // TAB_CONFIG entry — guard against undefined.
  //
  // EACH ROLE SEES ONLY WHAT IT OWNS. There used to be a single rule — hide if
  // marked superAdminOnly — and no opposite rule, so anything unmarked showed to
  // BOTH roles. That is why Super Admin was offered "All Patient Mode" and
  // "Patient Library": nobody had marked them, not because it was intended.
  //
  //   superAdminOnly -> platform-wide (shared login/signup, patient screens):
  //                     Super Admin only.
  //   tenantOwned    -> one hospital's own switch: that hospital's admin only.
  //                     Super Admin has no hospital, so there is no row for them
  //                     to write; they set these per tenant from Tenants →
  //                     Features. Backend/config/featureOwnership.js enforces
  //                     the same split, so a hidden control is also a refused
  //                     request.
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  const tabs = [
    { id: 'login', label: t('settings.login', 'Login'), icon: KeyRound },
    // Doctor tab's only tenant-owned content (All Patient Mode, Leave Apply
    // Future Upto — see TAB_CONFIG.doctor) is medi_consult data, so a
    // pharmacy/lab/radiology-only tenant admin has nothing to set here.
    ...((isSuperAdmin || hasFeature('appointments')) ? [{ id: 'doctor', label: t('settings.doctor', 'Doctor'), icon: Stethoscope }] : []),
    // Patient tab: the hospital's own Patient ID prefix card — tenant admin
    // only. Super Admin had the platform-wide patient switches here; those
    // were retired (1 Oct 2026), so the tab is gone for them.
    ...(!isSuperAdmin ? [{ id: 'patient', label: t('settings.patient', 'Patient'), icon: Users }] : []),
    { id: 'payments', label: t('settings.payments', 'Payments'), icon: CreditCard },
    // The organisation's own plan from Mitra One Care (doc/5.10.2026.md) —
    // its admin only, and not for a No fee organisation (nothing to show).
    ...(!isSuperAdmin && billing?.kind !== 'no_fee'
      ? [{ id: 'subscription', label: t('tenantSubscription.tab', 'Subscription'), icon: BadgeIndianRupee }] : []),
  ];

  const ownedByCaller = (item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.tenantOwned) return !isSuperAdmin;
    return true;
  };
  const currentToggles = (TAB_CONFIG[activeTab]?.toggles || []).filter(ownedByCaller);
  const currentSliders = (TAB_CONFIG[activeTab]?.sliders || []).filter(ownedByCaller);

  return (
    <div className="w-full" data-testid="settings-container">
      <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
        {/* Heading */}
        <div className="flex items-center gap-3 mb-6" data-testid="settings-header">
          {/* <div className="p-2 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-xl shadow-md">
            <Settings2 className="w-6 h-6 text-white" />
          </div> */}
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
              data-testid="settings-title"
            >
              {t('settings.title', 'Settings')}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-testid="settings-subtitle">
              {t(
                'settings.subtitle',
                'Control which features doctors and patients see across the app.'
              )}
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 mb-6" data-testid="settings-tabs">
          <nav className="flex gap-2" data-testid="settings-tabs-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`settings-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    isActive
                      ? 'border-[#4ABDBD] text-[#4ABDBD]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Payments is a custom admin tab; the doctor/patient tabs use the toggle grid.
            A tenant admin's Patient tab has no toggles but DOES have the
            Patient ID prefix card, so it must not fall into the empty state. */}
        {activeTab === 'subscription' ? (
          <TenantSubscriptionTab />
        ) : activeTab !== 'payments' && !(activeTab === 'patient' && !isSuperAdmin)
          && currentToggles.length === 0 && currentSliders.length === 0 ? (
          <div data-testid="settings-no-owned-settings" className="text-center py-12 px-6">
            <p className="text-sm font-semibold text-[#1a365d]">
              {isSuperAdmin
                ? t('settings.none_for_super_admin', 'These are per-hospital settings')
                : t('settings.none_for_tenant_admin', 'These are platform-wide settings')}
            </p>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
              {isSuperAdmin
                ? t('settings.none_for_super_admin_hint', 'Each hospital controls these for itself. To set them for a specific hospital, open Tenants → pick the tenant → Features.')
                : t('settings.none_for_tenant_admin_hint', 'These affect the shared sign-up pages and every patient on the platform, so they are managed centrally. Contact platform support if you need one changed.')}
            </p>
          </div>
        ) : activeTab === 'payments' ? (
          // Super Admin's payment screen is MAINTENANCE (hold a method during
          // an outage); a hospital's is its own configuration. Different
          // questions, so different components.
          isSuperAdmin ? <PlatformPaymentsTab /> : <AdminPaymentsTab />
        ) : (
        <>
        {/* The hospital's own patient numbering — tenant admin only (the
            Super Admin overrides a specific tenant's prefix from Tenants →
            open the tenant, not from their own Settings). */}
        {activeTab === 'patient' && !isSuperAdmin && <PatientCodeCard />}

        {/* Toggles for the active tab */}
        {currentToggles.length > 0 && (
        <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100" data-testid={`settings-toggles-list-${activeTab}`}>
          {currentToggles.map((toggle) => {
            const flag = flags[toggle.fekey];
            const checked = flag?.status === 'active';
            return (
              <div
                key={toggle.fekey}
                data-testid={`settings-toggle-row-${toggle.fekey}`}
                className="flex items-start sm:items-center justify-between gap-4 p-4 sm:p-5"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 text-sm sm:text-base" data-testid={`settings-toggle-label-${toggle.fekey}`}>
                    {t(`settings.toggles.${toggle.fekey}.label`, toggle.label)}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1" data-testid={`settings-toggle-description-${toggle.fekey}`}>
                    {t(`settings.toggles.${toggle.fekey}.description`, toggle.description)}
                  </p>
                </div>
                <ToggleSwitch
                  checked={checked}
                  onChange={() => handleToggle(toggle.fekey)}
                  disabled={featureLoading || toggling}
                  testId={`settings-toggle-${toggle.fekey}`}
                />
              </div>
            );
          })}
        </div>
        )}

        {/* Stepped sliders for multi-value settings on the active tab */}
        {currentSliders.length > 0 && (
          <div className="mt-6 space-y-6" data-testid={`settings-sliders-list-${activeTab}`}>
            {currentSliders.map((slider) => {
              const flag = flags[slider.fekey];
              const options = slider.options.map((o) => ({
                ...o,
                label: sliderOptionLabel(o),
              }));
              const current = flag?.value ?? slider.defaultValue ?? options[options.length - 1].value;
              return (
                <div
                  key={slider.fekey}
                  data-testid={`settings-slider-row-${slider.fekey}`}
                  className="rounded-2xl border border-gray-200 p-4 sm:p-5"
                >
                  <div className="mb-5 min-w-0">
                    <div
                      className="font-semibold text-gray-800 text-sm sm:text-base"
                      data-testid={`settings-slider-label-${slider.fekey}`}
                    >
                      {t(`settings.sliders.${slider.fekey}.label`, slider.label)}
                    </div>
                    <p
                      className="text-xs sm:text-sm text-gray-500 mt-1"
                      data-testid={`settings-slider-description-${slider.fekey}`}
                    >
                      {t(`settings.sliders.${slider.fekey}.description`, slider.description)}
                    </p>
                  </div>
                  <div className="px-1 sm:px-2">
                    <SegmentedSlider
                      options={options}
                      value={current}
                      onChange={(value) => handleSliderChange(slider.fekey, value)}
                      disabled={featureLoading || settingValue}
                      testId={`settings-slider-${slider.fekey}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default Settings;
